import type { DashboardLayoutType } from '@/api/dashboardsApi'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { SkeletonRegion, fitCount, useElementSize, useStableKeys } from './_shared'

export interface LedSkeletonProps {
	/** Fixed row count. Omit to scale the count to the measured height. */
	rows?: number
	/** Fixed column count. Omit to scale the count to the measured width. */
	cols?: number
	className?: string
	label?: string | false
	dashboardType?: DashboardLayoutType
}

// Vary header-label widths so they read naturally (fleet dashboard table style)
const HEADER_WIDTHS = ['w-10', 'w-20', 'w-28', 'w-24', 'w-12', 'w-10', 'w-14']

const FLEET_HEADER_HEIGHT = 37
const FLEET_ROW_HEIGHT = 52
const FLEET_COL_WIDTH = 110
// Width of the leading device-ID column, excluded from the space the LED columns share.
const FLEET_DEVICE_COL_WIDTH = 40
// p-4 on both sides of the device-variant bulb grid.
const DEVICE_GRID_PADDING = 32
// Bulb + label cell, including the flex gaps around it.
const DEVICE_BULB_WIDTH = 88
const DEVICE_BULB_HEIGHT = 68

// Faux device dashboard column label widths representing distance_travelled, config_version, etc.
const DEVICE_LABEL_WIDTHS = ['w-20', 'w-24', 'w-16', 'w-12', 'w-14', 'w-8', 'w-16', 'w-10', 'w-28', 'w-14', 'w-12']

export function LedSkeleton({
	rows,
	cols,
	className,
	label = 'Loading LEDs',
	dashboardType = 'fleet',
}: LedSkeletonProps) {
	const [rootRef, size] = useElementSize<HTMLDivElement>()

	const rowCount = rows ?? fitCount(size.height - FLEET_HEADER_HEIGHT, FLEET_ROW_HEIGHT, 1, 40)
	const colCount = cols ?? fitCount(size.width - FLEET_DEVICE_COL_WIDTH, FLEET_COL_WIDTH, 2, 10)
	const rowKeys = useStableKeys(rowCount)
	const colKeys = useStableKeys(colCount)
	const headerKeys = useStableKeys(colCount)

	// Device variant: bulbs wrap into a grid, so the count follows the panel area.
	const deviceBulbKeys = useStableKeys(
		fitCount(size.width - DEVICE_GRID_PADDING, DEVICE_BULB_WIDTH, 1, 8) *
			fitCount(size.height - DEVICE_GRID_PADDING, DEVICE_BULB_HEIGHT, 1, 6),
	)

	// 1. Device Dashboard LED Panel Skeleton
	if (dashboardType === 'device') {
		return (
			<SkeletonRegion
				ref={rootRef}
				label={label}
				className={cn('flex size-full p-4 overflow-hidden scrollbar-hide', className)}>
				<div className='flex flex-wrap gap-x-8 gap-y-6 w-full max-w-5xl'>
					{deviceBulbKeys.map((key, i) => (
						<div key={key} className='flex flex-col items-center gap-2 shrink-0'>
							<Skeleton className='size-[30px] rounded-full' />
							<Skeleton
								className={cn('h-3.5 bg-accent/30 rounded', DEVICE_LABEL_WIDTHS[i % DEVICE_LABEL_WIDTHS.length])}
							/>
						</div>
					))}
				</div>
			</SkeletonRegion>
		)
	}

	// 2. Fleet Dashboard LED Panel Skeleton (Table Rows Style)
	return (
		<SkeletonRegion
			ref={rootRef}
			label={label}
			className={cn('flex size-full min-h-0 flex-col overflow-hidden', className)}>
			{/* Header row — mirrors the sticky thead with column names */}
			<div className='flex shrink-0 items-center border-b px-2 py-2.5 gap-0'>
				{/* Device ID column header */}
				<div className='w-10 shrink-0 px-2'>
					<Skeleton className='h-3 w-8' />
				</div>
				{/* LED column headers */}
				{headerKeys.map((key, i) => (
					<div key={key} className='flex flex-1 justify-center px-2'>
						<Skeleton className={cn('h-3', HEADER_WIDTHS[i % HEADER_WIDTHS.length])} />
					</div>
				))}
			</div>

			{/* Data rows — device ID cell + LED bulb cells */}
			<div className='flex min-h-0 flex-col divide-y overflow-hidden'>
				{rowKeys.map(rowKey => (
					<div key={rowKey} className='flex items-center gap-0 px-2 py-3'>
						{/* Device ID */}
						<div className='w-10 shrink-0 px-2'>
							<Skeleton className='h-3 w-4' />
						</div>
						{/* LED bulb skeletons — rounded-full to match LedBulb */}
						{colKeys.map(colKey => (
							<div key={colKey} className='flex flex-1 justify-center px-2'>
								<Skeleton className='size-control rounded-full' />
							</div>
						))}
					</div>
				))}
			</div>
		</SkeletonRegion>
	)
}
